import { clearPnpSessionStorage, PIQ_SESSION_TOKEN_KEY } from '../../components/trustlyIframe/trustlyUtils.ts';
import { BeErrorEnvelope, parseBeError } from '../../flow/errors/beErrorResponse.ts';
import { buildAppError } from '../../flow/errors/errorBuilder.ts';
import { FE_ERROR_CODES, FeErrorCode, mapBeReasonToFe } from '../../flow/errors/errorCodes.ts';
import { useRegistrationStore } from '../../store/store.ts';
import { HEADER_NAMES } from '../../utils/constants.ts';
import { buildDeviceInfo, PnpDeviceInfo } from '../../utils/deviceContext.ts';
import { CASUMO_BRAND_ID } from '../../utils/envConstants.ts';
import { gatekeeper } from '../../utils/gatekeeper.ts';
import { GtmEvents } from '../../utils/gtm/gtm.ts';
import { getMarketLobbyUrl, getPnpLoginUrl } from '../../utils/utils.ts';

export type PnpLoginRequest = {
  sessionToken: string;
  deviceInfo: PnpDeviceInfo;
};

export type PnpLoginResponse = {
  success?: boolean;
  sessionId: string;
  depositResult?: {
    success: boolean;
    amount: number;
    currency: string;
    createdAt: string | number;
  };
};

type PnpLoginErrorBody = BeErrorEnvelope & {
  reason?: string;
  status?: string;
  loginResponse?: { reason?: string };
};

type PnpLoginBody = PnpLoginResponse & PnpLoginErrorBody;

const LOGIN_PASSTHROUGH_CODES: FeErrorCode[] = [
  FE_ERROR_CODES.registration_timed_out,
  FE_ERROR_CODES.piq_transaction_refund,
];

export const pnpLogin = async (payload: PnpLoginRequest): Promise<PnpLoginResponse> => {
  let response: Response;
  const platformFetch = gatekeeper?.fetch ?? globalThis.fetch.bind(globalThis);

  try {
    response = await platformFetch(getPnpLoginUrl(), {
      method:      'POST',
      credentials: 'include',
      headers:     {
        [HEADER_NAMES.CASUMO_BRAND_ID]: `${CASUMO_BRAND_ID}`,
        Accept:                         'application/json',
        'Content-Type':                 'application/json',
        [HEADER_NAMES.USER_AGENT]:      navigator?.userAgent ?? '',
      },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw buildAppError(FE_ERROR_CODES.pnp_login_failed, { originalError: err });
  }

  const data: PnpLoginBody | undefined = await response.json().catch(() => undefined);
  const beError = parseBeError(data);

  if (beError) {
    throw buildAppError(
      beError.feCode === FE_ERROR_CODES.unknown_error
        ? FE_ERROR_CODES.pnp_login_failed
        : beError.feCode,
      { status: response.status, ...data, ...beError.cause },
    );
  }

  if (!response.ok) {
    const beReason = data?.reason ?? data?.loginResponse?.reason ?? data?.status;
    const feCode = mapBeReasonToFe(beReason);

    throw buildAppError(
      LOGIN_PASSTHROUGH_CODES.includes(feCode) ? feCode : FE_ERROR_CODES.pnp_login_failed,
      { status: response.status, ...data },
    );
  }

  if (!data) {
    throw buildAppError(FE_ERROR_CODES.pnp_login_failed, { status: response.status });
  }

  return data;
};

export const performPnpAutoLogin = async (): Promise<void> => {
  const { setMetadata, setRootLoading } = useRegistrationStore.getState();

  setRootLoading(true);

  try {
    const sessionToken = sessionStorage.getItem(PIQ_SESSION_TOKEN_KEY);
    if (!sessionToken) {
      throw buildAppError(FE_ERROR_CODES.pnp_login_failed, { missingSessionToken: true });
    }

    const deviceInfo = await buildDeviceInfo();
    const data = await pnpLogin({ sessionToken, deviceInfo });

    if (data.success === false || data.depositResult?.success === false) {
      throw buildAppError(FE_ERROR_CODES.pnp_deposit_unsuccessful, { status: 200 });
    }

    GtmEvents.logPnpAutoLoginDone(true);

    clearPnpSessionStorage();
    setRootLoading(false);
    setMetadata({ isOpen: false });
    window.location.href = getMarketLobbyUrl();
  } catch (err) {
    setRootLoading(false);

    const error = err instanceof Error
      ? err
      : buildAppError(FE_ERROR_CODES.pnp_login_failed);
    GtmEvents.logPnpAutoLoginDone(false, error.name);

    throw error;
  }
};
